// noinspection LocalVariableNamingConventionJS

// Removed DialogProgrammatic prompt usage
import { paramCase, pascalCase } from 'change-case';
import cloneDeep from 'lodash/cloneDeep.js';
import first from 'lodash/first.js';
import get from 'lodash/get.js';
import has from 'lodash/has.js';
import map from 'lodash/map.js';
import set from 'lodash/set.js';
import toArray from 'lodash/toArray.js';
// import values from 'lodash/values.js'; // removed; no longer used
import pluralize from 'pluralize';
import uuidv4 from 'uuid/v4.js';

import { handleResponseError } from '@/Helpers.js';
import Activity from '@/models/Activity.js';
import ActivityTemplate from '@/models/ActivityTemplate.js';
import AutomatedSurvey from '@/models/AutomatedSurvey.js';
import CourseTemplate from '@/models/CourseTemplate.js';
import Section from '@/models/Section.js';
import SectionTemplate from '@/models/SectionTemplate.js';
import TimePeriod from '@/models/TimePeriod.js';
import TimePeriodTemplate from '@/models/TimePeriodTemplate.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';
import CourseFormHelpers from '@/store/utilities/CourseFormHelpers.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';
import RecursiveSearch from '@/utilities/RecursiveSearch.js';

// Dialog prompt removed; default counts are applied without prompting
function pluralNamespaceForType(type) {
    return pluralize(pascalCase(type));
}

const searchParams = {
    targetKey: 'uuid',
    container: 'items',
};

async function rollbackCreatedWeeks(context, payload, sections) {
    if (!sections?.length) {
        return;
    }

    const namespace = pluralNamespaceForType(payload.type);

    await Promise.all(sections.map(section => context.dispatch(`${namespace}/remove`, section.uuid, { root: true })));

    await context.dispatch('reorder', {
        array: context.getters.courseItem('items'),
    });
}

function findItemByUuid(items, uuid) {
    const directMatch = items.find(item => item.uuid === uuid);
    if (directMatch) return directMatch;

    const foundInChildren = items
        .filter(item => item.items && item.items.length)
        .map(item => findItemByUuid(item.items, uuid))
        .find(result => result !== null);

    return foundInChildren || null;
}

export default function BaseCourseTemplateForm({ activityType, sectionType }) {
    function extractFlatItemsFromNested(items) {
        const sections = [];
        const activities = [];

        function processItems(itemList) {
            for (const item of itemList) {
                // Ensure namespace is set for Vuex store access
                if (!item.namespace) {
                    const isTemplate = item.type.includes('template');
                    if (item.type.includes('section') || item.type === 'section') {
                        item.namespace = isTemplate ? 'SectionTemplates' : 'Sections';
                    } else if (item.type.includes('activity') || item.type === 'activity') {
                        item.namespace = isTemplate ? 'ActivityTemplates' : 'Activities';
                    }
                }

                if (item.type === 'section' || item.type.includes('section')) {
                    sections.push(item);
                } else if (item.type === 'activity' || item.type.includes('activity')) {
                    activities.push(item);
                }

                if (item.items && Array.isArray(item.items)) {
                    processItems(item.items);
                }
            }
        }

        processItems(items);
        return { sections, activities };
    }

    async function performApiDeletion(context, namespace, item) {
        if (!item) return null;

        const url = `/api/${paramCase(namespace)}/${item.id}`;
        const response = await context.dispatch('AutoSave/remove', { url }, { root: true }).then(deleteResponse => {
            context.dispatch('AutoSave/setSaved', 'CourseTemplateForm/deleteCourseItem', {
                root: true,
            });
            return deleteResponse?.response;
        });

        if (response?.status >= 400 && response?.status < 600) {
            throw new Error('Error detected in delete action, halting process');
        }

        return null;
    }

    async function performStateDeletion(context, namespace, uuid) {
        await context.commit('deleteCourseItem', {
            uuid,
            field: 'delete',
        });

        await context.dispatch(`${namespace}/remove`, uuid, {
            root: true,
        });
    }

    async function deleteSectionTemplate(item, context) {
        await SectionTemplate.delete(item.id);
        await context.dispatch('Menu/setMenu', CourseTemplate.find(item.course_template_id).menu(), {
            root: true,
        });
        TimePeriodTemplate.query().where('section_template_id', item.id).delete();
    }

    async function deleteSection(item) {
        await Section.delete(item.id);
        TimePeriod.query().where('section_id', item.id).delete();
    }

    async function deleteActivityTemplate(item, courseTemplateId, context) {
        await ActivityTemplate.delete(item.id);
        await context.dispatch('Menu/setMenu', CourseTemplate.find(courseTemplateId).menu(), {
            root: true,
        });
    }

    async function deleteActivity(item) {
        await Activity.delete(item.id);
    }

    async function performModelDeletion(item, courseTemplateId, context) {
        if (!item) return;

        switch (item.type) {
            case 'section_template':
                await deleteSectionTemplate(item, context);
                break;
            case 'section':
                await deleteSection(item);
                break;
            case 'activity_template':
                await deleteActivityTemplate(item, courseTemplateId, context);
                break;
            case 'activity':
                await deleteActivity(item);
                break;
            default:
                // No specific deletion logic for this item type
                break;
        }
    }

    function handlePostDeletion(context) {
        if (context.getters.courseItem('items').length === 0) {
            return null;
        }

        return context.dispatch('reorder', {
            array: context.state.form.course.items,
        });
    }

    return {
        state: {
            assignableTags: [],

            form: {
                course: {
                    id: '',
                    title: '',
                    description: '',
                    course_type_id: null,
                    course_settings: null,
                    external_id: null,
                    items: [],
                    tags: [],
                },
            },

            pendingScroll: null,
            courseKeyIntegrationInProgress: false,
            courseKeyIntegrationError: null,
            hydrating: false,
        },

        getters: {
            course: state => state.form.course,

            courseItem: state => field => get(state.form.course, field),

            isHydrating: state => state.hydrating,

            courseHasItemWithNoSetId:
                (state, getters, rootState, rootGetters) =>
                ({ template }) => {
                    const activityNamespace = template ? 'ActivityTemplates' : 'Activities';
                    const sectionNamespace = template ? 'SectionTemplates' : 'Sections';

                    const activities = toArray(rootGetters[`${activityNamespace}/all`]());
                    const sections = toArray(rootGetters[`${sectionNamespace}/all`]());

                    return activities.some(activity => !activity.id) || sections.some(section => !section.id);
                },

            get: state => (id, field, type) => {
                if (type === 'activity_template' || type === 'activity') {
                    return null;
                }

                const search = new RecursiveSearch();

                return search.get(state.form.course.items, {
                    ...searchParams,
                    subjectKey: id,
                    field,
                });
            },

            level: state => {
                const search = new RecursiveSearch();

                return search.depthLevel(state.form.course.items, 'items');
            },

            prerequisites: (state, getters, rootState, rootGetters) => uuid => {
                const helper = new CourseFormHelpers({
                    state,
                    getters,
                    rootState,
                    rootGetters,
                });

                return helper.getPrerequisites(uuid);
            },
        },

        actions: {
            add(context, payload) {
                return new Promise(resolve => {
                    if (get(payload, 'parent')) {
                        context.commit('addSectionItem', payload);
                    } else {
                        context.commit('addCourseItem', payload);
                    }

                    resolve();
                });
            },

            async addWeekSections(context, { payload, numWeeks = null }) {
                const addSections = async value => {
                    let sections = null;

                    try {
                        sections = await context.dispatch('createWeeks', {
                            value,
                            payload,
                        });

                        const orderedSections = await context.dispatch(
                            `${pluralNamespaceForType(payload.type)}/persist`,
                            { sections },
                            { root: true }
                        );

                        context.commit('pendingScroll', first(orderedSections));

                        const initialItems = context.getters.courseItem('items');
                        let insertIndex = initialItems.length;

                        if (payload.insertAfter) {
                            const insertAfterItem = findItemByUuid(initialItems, payload.insertAfter);
                            if (insertAfterItem) {
                                const afterIndex = initialItems.findIndex(item => item.uuid === insertAfterItem.uuid);
                                if (afterIndex !== -1) {
                                    insertIndex = afterIndex + 1;
                                }
                            }
                        }

                        // Insert sequentially from a single computed anchor, advancing the index
                        // after each insertion so multi-section inserts preserve the backend order
                        // instead of reversing it (every section previously landed at the same index).
                        orderedSections.forEach(section => {
                            const uiSection = {
                                ...section,
                                namespace: section.namespace || pluralNamespaceForType(section.type),
                                isCollapsed: false,
                                isLocalCollapsed: false,
                                displayStatus: { editing: true },
                                isNewlyCreated: true,
                            };

                            context.commit('insertCourseItemAtPosition', { item: uiSection, index: insertIndex });
                            insertIndex += 1;
                        });

                        await context.dispatch('reorder', {
                            array: context.getters.courseItem('items'),
                        });

                        return orderedSections;
                    } catch (error) {
                        await rollbackCreatedWeeks(context, payload, sections);

                        if (error?.response?.status !== 422) {
                            throw error;
                        }

                        return null;
                    }
                };

                // If caller did not specify a count, fall back to 1 without prompting
                if (numWeeks === null) {
                    return addSections(1);
                }

                return addSections(numWeeks);
            },

            async createWeeks(context, { value, payload }) {
                const courseType = payload.type.includes('temp') ? 'course_template_id' : 'course_id';
                let items = [];
                let i = 0;

                const finalSectionType = payload.sectionType === 'section' ? 'topic' : payload.sectionType;

                let order = 0;
                let parent = null;

                if (payload.insertAfter) {
                    const allItems = context.getters.courseItem('items');

                    const insertAfterItem = findItemByUuid(allItems, payload.insertAfter);

                    if (insertAfterItem) {
                        order = insertAfterItem.order + 1;
                        // Weeks/Breaks must always be root-level; never inherit a parent
                        parent = null;

                        const search = new RecursiveSearch();
                        search.update(allItems, {
                            ...searchParams,
                            action: target => {
                                if (target.order >= order) {
                                    target.order += 1;
                                }
                            },
                        });
                    } else {
                        order = context.getters.courseItem('items').length;
                    }
                } else {
                    order = context.getters.courseItem('items').length;
                }

                while (i < value) {
                    items.push({
                        [courseType]: context.getters.courseItem('id'),
                        items: [],
                        order: order + i,
                        parent,
                        sectionType: finalSectionType,
                        type: payload.type,
                        uuid: uuidv4(),
                        isLocalCollapsed: false,
                        isCollapsed: false,
                        displayStatus: { editing: true },
                    });

                    i++;
                }

                items = await Promise.all(
                    items.map((item, index) => {
                        context.dispatch(`${pluralNamespaceForType(item.type)}/add`, item, {
                            root: true,
                        });

                        context.commit(
                            `${pluralNamespaceForType(item.type)}/${item.uuid}/SET_SETTINGS`,
                            {
                                type: item.sectionType,
                            },
                            { root: true }
                        );

                        if (item.sectionType === 'week' || item.sectionType === 'break') {
                            const weekNumber = index + 1;

                            context.commit(
                                `${pluralNamespaceForType(item.type)}/${item.uuid}/EDIT_SETTINGS`,
                                {
                                    settings: 'section_settings_template',
                                    field: 'week_number',
                                    value: weekNumber,
                                },
                                { root: true }
                            );

                            context.commit(
                                `${pluralNamespaceForType(item.type)}/${item.uuid}/EDIT_SETTINGS`,
                                {
                                    settings: 'section_settings_template',
                                    field: 'display_week_number',
                                    value: weekNumber,
                                },
                                { root: true }
                            );

                            const storeItem =
                                context.rootGetters[`${pluralNamespaceForType(item.type)}/${item.uuid}/get`];
                            if (storeItem && typeof storeItem !== 'function') {
                                // Dev note: week settings created for item
                                // (console output removed to satisfy linter)
                            }
                        }

                        return item;
                    })
                );

                return items;
            },

            async delete(context, { namespace, uuid, course_template_id }) {
                if (!uuid) {
                    throw new Error(
                        `The uuid is required when attempting to delete an item, received argument: ${namespace}`
                    );
                }

                const item = context.rootGetters[`${namespace}/get`](uuid);

                await performApiDeletion(context, namespace, item);
                await performStateDeletion(context, namespace, uuid);
                await performModelDeletion(item, course_template_id, context);

                return handlePostDeletion(context);
            },

            deleteChildren(context, { items, uuid }) {
                const search = new RecursiveSearch();
                const children = [];

                search.do(items, searchParams.container, target => {
                    if (target.uuid !== uuid) {
                        const module = pluralNamespaceForType(target.type);

                        if (!has(target, 'id')) {
                            target.id = context.rootGetters[`${module}/${target.uuid}/get`]('id');
                        }

                        children.push({
                            url: `/api/${pluralize(paramCase(target.type))}/${target.id}`,
                        });

                        context.dispatch(`${module}/remove`, target.uuid, {
                            root: true,
                        });
                    }
                });

                Promise.all(
                    children.map(child => context.dispatch('AutoSave/remove', { url: child.url }, { root: true }))
                ).then(() => {
                    context.dispatch('AutoSave/setSaved', 'CourseTemplateForm/deleteCourseItem', {
                        root: true,
                    });
                });
            },

            deleteCourse(context, { id }) {
                return axios.delete(`/api/course-templates/${id}`);
            },

            async hydrate(context, { payload }) {
                const shouldManageHydrating = !context.state.hydrating;

                if (shouldManageHydrating) {
                    context.commit('SET_HYDRATING', true);
                }

                try {
                    const template = !has(payload, 'course_template_id');
                    const activityKey = template ? 'activity_templates' : 'activities';
                    const sectionKey = template ? 'section_templates' : 'sections';
                    const gradingSchemeIdKey = template ? 'grading_scheme_template_id' : 'grading_scheme_id';
                    const apiType = template ? 'grading-scheme-templates' : 'grading-schemes';

                    const gradingSchemeIds = [];

                    const queryBuilder = new QueryBuilder(`${apiType}`);

                    const search = new RecursiveSearch();

                    // Extract flat arrays from nested items structure if needed
                    if (payload.items && (!payload[sectionKey] || !Array.isArray(payload[sectionKey]))) {
                        const flatItems = extractFlatItemsFromNested(payload.items);
                        payload[sectionKey] = flatItems.sections;
                        payload[activityKey] = flatItems.activities;
                    }

                    context.commit('updateCourseField', {
                        field: 'course_settings',
                        value: get(payload, 'course_settings'),
                    });

                    search.do(payload.items, 'items', item => {
                        if (item.type.includes('sec') && !has(item, 'items')) {
                            item.items = [];
                        }

                        // Ensure essential fields exist with fallbacks
                        if (!item.title && item.title !== '') {
                            item.title = '';
                        }

                        // Ensure namespace is set for proper store access
                        if (!item.namespace) {
                            if (item.type.includes('act')) {
                                item.namespace = template ? 'ActivityTemplates' : 'Activities';
                            } else {
                                item.namespace = template ? 'SectionTemplates' : 'Sections';
                            }
                        }
                    });

                    if (Array.isArray(payload.items) && payload.items.length > 0) {
                        context.commit('updateCourseField', {
                            field: 'items',
                            value: payload.items,
                        });
                    }

                    payload[activityKey].forEach(item => {
                        if (get(item, gradingSchemeIdKey)) {
                            gradingSchemeIds.push(item[gradingSchemeIdKey]);
                        }
                    });

                    queryBuilder.filter('id', gradingSchemeIds);

                    queryBuilder.raw({ per_page: 200 });

                    const gradingSchemes = await axios.get(queryBuilder.toUrl()).then(resp => resp.data.data);

                    const sectionModule = template ? 'SectionTemplates' : 'Sections';
                    const activityModule = template ? 'ActivityTemplates' : 'Activities';
                    await context.dispatch(`${sectionModule}/purge`, {}, { root: true });
                    await context.dispatch(`${activityModule}/purge`, {}, { root: true });

                    await Promise.all(
                        payload[sectionKey].map(async section => {
                            const module = pascalCase(pluralize(section.type));

                            await context.dispatch(`${module}/hydrateSection`, { payload: section }, { root: true });
                        })
                    );

                    await Promise.all(
                        payload[activityKey].map(async activity => {
                            const module = pascalCase(pluralize(activity.type));

                            if (get(activity, gradingSchemeIdKey)) {
                                const index = gradingSchemes.findIndex(
                                    item => item.id === activity[gradingSchemeIdKey]
                                );

                                activity.grading_scheme = gradingSchemes[index];
                            }

                            await context.dispatch(`${module}/hydrateActivity`, { payload: activity }, { root: true });
                        })
                    );

                    // Establish inverse prerequiste relationships if any exist

                    search.do(payload.items, 'items', item => {
                        if (get(item, 'prerequisite_id')) {
                            const namespace = pluralNamespaceForType(item.prerequisite.type);

                            const recipientUuid = context.rootGetters[`${namespace}/getByKey`]({
                                keyName: 'id',
                                key: Number(item.prerequisite_id),
                                field: 'uuid',
                            });

                            context.dispatch(
                                `${pluralNamespaceForType(item.type)}/setInversePrerequisites`,
                                {
                                    initiator: item.uuid,
                                    recipient: recipientUuid,
                                },
                                { root: true }
                            );
                        }
                    });

                    await context.dispatch('hydrateGradingScheme', {
                        course: payload,
                    });

                    await context.dispatch(`${pluralNamespaceForType(sectionKey)}/assignWeekOrder`, true, {
                        root: true,
                    });

                    // Merge hydrated data back into nested items structure
                    search.do(payload.items, 'items', item => {
                        // Set namespace if missing
                        if (!item.namespace) {
                            const pluralType = item.type.includes('act') ? 'Activities' : 'Sections';
                            item.namespace = template ? `${pluralType.slice(0, -1)}Templates` : pluralType;
                        }

                        // Try to get hydrated data from Vuex store and merge it into the item
                        try {
                            // Store access for merging

                            // Get data directly from store state (bypass problematic getter)
                            const directStoreItem =
                                context.rootState[item.namespace] && context.rootState[item.namespace][item.uuid];

                            if (directStoreItem && typeof directStoreItem !== 'function') {
                                // Merge hydrated data into the nested item
                                Object.assign(item, {
                                    title: directStoreItem.title || item.title,
                                    description: directStoreItem.description || item.description,
                                    activity_type: directStoreItem.activity_type || item.activity_type,
                                    sectionType: directStoreItem.sectionType || item.sectionType,
                                    section_settings: directStoreItem.section_settings || item.section_settings,
                                    section_settings_template:
                                        directStoreItem.section_settings_template || item.section_settings_template,
                                    activity_settings: directStoreItem.activity_settings || item.activity_settings,
                                    // Do not force-collapse. Preserve existing collapse state and only override when explicitly provided by store
                                    displayStatus: directStoreItem.displayStatus ||
                                        item.displayStatus || { editing: false },
                                });

                                // Preserve existing isCollapsed unless store explicitly defines it
                                if (directStoreItem.isCollapsed !== undefined) {
                                    item.isCollapsed = directStoreItem.isCollapsed;
                                } else if (item.isCollapsed === undefined) {
                                    // Leave undefined to allow component logic to determine default without mass-collapsing
                                }

                                // Successfully merged data
                            }
                        } catch (_error) {
                            // Error accessing store item
                        }

                        // Fallback title if still missing
                        if (!item.title && item.title !== '') {
                            item.title = 'Untitled';
                        }
                    });

                    // eslint-disable-next-line no-console
                    // console.log('merge results', { attempted: mergeAttemptCount, successful: mergeSuccessCount });

                    // Nested structure analysis completed

                    // Force reactivity update - create completely new object references
                    // This is CRITICAL to ensure Vue detects the changes
                    function deepCloneWithReactivity(item) {
                        const cloned = { ...item };
                        if (item.items && Array.isArray(item.items)) {
                            cloned.items = item.items.map(subItem => deepCloneWithReactivity(subItem));
                        }
                        return cloned;
                    }

                    const originalItems = payload.items;
                    payload.items = originalItems.map(item => deepCloneWithReactivity(item));

                    // eslint-disable-next-line no-console
                    // console.log('reactivity update complete');

                    context.commit('fill', payload);
                } finally {
                    if (shouldManageHydrating) {
                        context.commit('SET_HYDRATING', false);
                    }
                }
            },

            async hydrateGradingScheme(context, { course }) {
                const schemeType = has(course, 'course_template_id')
                    ? 'grading_scheme_id'
                    : 'grading_scheme_template_id';
                const isTemplate = schemeType.includes('temp');

                if (course[schemeType]) {
                    const { data } = await axios.get(
                        `/api/grading-scheme${isTemplate ? '-templates' : 's'}/${course[schemeType]}`
                    );

                    const gradingScheme = data.data;

                    gradingScheme.course_id = course.id;
                    gradingScheme.uuid = course.id;

                    context.dispatch(
                        `GradingScheme${isTemplate ? 'Templates' : 's'}/hydrate`,
                        {
                            grading_scheme: gradingScheme,
                        },
                        { root: true }
                    );
                }
            },

            async fetchTypes() {
                const { data } = await axios.get('/api/course-types');

                return data.data;
            },

            async fetchAssignableTags(context, { isTemplate }) {
                const tagType = isTemplate ? 'course_template' : 'course';

                const { data } = await axios.get(`/api/tags?type=${tagType}`);

                context.commit('setAssignableTags', data.data);
            },

            async reset(context) {
                context.commit('reset');

                await context.dispatch(`${activityType}/purge`, null, {
                    root: true,
                });

                await context.dispatch(`${sectionType}/purge`, null, {
                    root: true,
                });
            },

            async reorder(context, payload) {
                const search = new RecursiveSearch();

                await context.commit('insert', { payload, field: 'insert' });

                await context.commit('reIndex');

                const relationshipUpdates = [];

                await search.do(payload.array, searchParams.container, target => {
                    if (target.type && target.uuid) {
                        try {
                            const namespace = pluralNamespaceForType(target.type);

                            if (context.rootState[namespace] && context.rootState[namespace][target.uuid]) {
                                relationshipUpdates.push(
                                    context.dispatch(
                                        `${namespace}/${target.uuid}/assignRelationships`,
                                        { item: target },
                                        { root: true }
                                    )
                                );
                            }
                        } catch (error) {
                            // Silently ignore errors for items that don't support assignRelationships
                            console.warn(
                                `Skipping assignRelationships for ${target.type}/${target.uuid}:`,
                                error.message
                            );
                        }
                    }
                });

                await Promise.all(relationshipUpdates);

                await context.dispatch('updateSectionWeeks');

                await context.dispatch(
                    'AutoSave/updateOrder',
                    {
                        items: context.state.form.course.items,
                        mutation: 'CourseTemplateForm/autoSaveOrder',
                    },
                    { root: true }
                );
            },

            async updateSectionWeeks(context) {
                const isTemplate = context.getters.courseItem('items').some(item => item.type.includes('temp'));
                const sectionsType = isTemplate ? 'SectionTemplates' : 'Sections';
                const activitiesType = isTemplate ? 'ActivityTemplates' : 'Activities';

                await context.dispatch(`${sectionsType}/assignWeekOrder`, {}, { root: true });

                await context.dispatch(`${activitiesType}/updateActivitySettings`, {}, { root: true });
            },

            async applyNewCourseStartDate({ commit, dispatch, rootState, rootGetters, state }) {
                const toggleLoading = () => commit('CourseForm/TOGGLE_LOADING', {}, { root: true });

                try {
                    toggleLoading();

                    const payload = state.form.course.items;

                    const settings = state.form.course.course_settings;

                    const weeks = rootGetters['Sections/getWeeks']({
                        settings: 'section_settings',
                    });

                    settings.end_date = moment(settings.start_date)
                        .add(weeks.length, 'weeks')
                        .add(6, 'days')
                        .endOf('day')
                        .utc()
                        .format('YYYY-MM-DD HH:mm:ss');

                    const courseSettings = await axios
                        .put(`/api/course-settings/${settings.id}`, settings)
                        .then(resp => resp?.data?.data);

                    if (courseSettings) {
                        const clonedCourse = cloneDeep(courseSettings?.course);

                        delete courseSettings.course;

                        commit(
                            `Courses/${clonedCourse.id}/EDIT`,
                            {
                                field: 'course_settings',
                                value: courseSettings,
                            },
                            { root: true }
                        );

                        if (clonedCourse) {
                            commit(
                                `Courses/${clonedCourse.id}/EDIT`,
                                {
                                    field: 'is_accessible_to_students',
                                    value: clonedCourse.is_accessible_to_students,
                                },
                                { root: true }
                            );
                        }
                    }

                    await dispatch('updateSectionWeeks');

                    await commit('autoSaveOrder', {
                        field: 'save',
                        items: payload,
                    });

                    toggleLoading();
                } catch (e) {
                    if (rootState.CourseForm.loading) toggleLoading();

                    handleResponseError(e);
                }
            },

            async applyNewCourseEndDate({ commit, rootState, state }) {
                const toggleLoading = () => commit('CourseForm/TOGGLE_LOADING', {}, { root: true });

                try {
                    toggleLoading();

                    const settings = state.form.course.course_settings;

                    settings.end_date = moment(settings.end_date).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss');

                    const courseSettings = await axios
                        .put(`/api/course-settings/${settings.id}`, settings)
                        .then(resp => resp?.data?.data);

                    if (courseSettings) {
                        const clonedCourse = cloneDeep(courseSettings?.course);

                        delete courseSettings.course;

                        commit(
                            `Courses/${clonedCourse.id}/EDIT`,
                            {
                                field: 'course_settings',
                                value: courseSettings,
                            },
                            { root: true }
                        );

                        if (clonedCourse) {
                            commit(
                                `Courses/${clonedCourse.id}/EDIT`,
                                {
                                    field: 'is_accessible_to_students',
                                    value: clonedCourse.is_accessible_to_students,
                                },
                                { root: true }
                            );
                        }
                    }

                    toggleLoading();
                } catch (e) {
                    if (rootState.CourseForm.loading) toggleLoading();

                    handleResponseError(e);
                }
            },

            /**
             * Persist a partial `course_settings` update to the server.
             *
             * `Model.$update()` is a local-only Vuex-ORM mutation (no HTTP), so
             * tabs that must actually save settings go through this real PUT —
             * the same endpoint the date actions above use. Rejects on failure so
             * the caller can revert its optimistic UI; does not swallow the error.
             */
            async persistCourseSettings(_context, { id, payload }) {
                const { data } = await axios.put(`/api/course-settings/${id}`, payload);

                return data?.data;
            },

            async duplicate(context, item) {
                try {
                    const { remediation } = item;

                    const helpers = new CourseFormHelpers(context);

                    await helpers.duplicate(item);

                    if (!remediation) await context.dispatch('updateSectionWeeks');
                } catch (e) {
                    console.error('error in duplication', e);
                }
            },

            async syncGroups(context, { isTemplate }) {
                const type = isTemplate ? 'CourseTemplates' : 'Courses';

                const endpoint = isTemplate ? 'course-template-groups' : 'course-groups';

                const modelKey = isTemplate ? 'course_template_id' : 'course_id';

                const item = context.getters.course;

                await context.dispatch('AutoSave/setSaving', `${type}/updateGroups`, { root: true });

                const { data } = await axios.put(
                    `/api/${endpoint}/sync`,
                    {
                        [modelKey]: item.id,
                        groups: map(item.groups, 'id'),
                    },
                    { errorHandle: false }
                );

                const removedGroupIds = data.data.groups.detached;

                await AutomatedSurvey.delete(automatedSurvey => removedGroupIds.includes(automatedSurvey.group_id));

                await context.dispatch('AutoSave/setSaved', `${type}/updateGroups`, { root: true });
            },

            async syncCourseCodes(context, { isTemplate, paramKey, courseCode }) {
                const type = isTemplate ? 'CourseTemplates' : 'Courses';

                const endpoint = isTemplate ? 'course-templates' : 'courses';

                const item = context.getters.course;

                await context.dispatch('AutoSave/setSaving', `${type}/updateCourseCodes`, { root: true });

                await axios.put(
                    `/api/${endpoint}/${item.id}`,
                    {
                        course_type_id: item.course_type_id,
                        course_settings: item.course_settings,
                        title: item.title,
                        enrollment_capacity: item.enrollment_capacity,
                        course_settings_template: item.course_settings_template,
                        [paramKey]: [courseCode],
                    },
                    {
                        errorHandle: false,
                    }
                );

                await context.dispatch('AutoSave/setSaved', `${type}/updateCourseCodes`, { root: true });
            },
        },

        mutations: {
            autoSaveOrder() {
                // used for AutoSave
            },

            addCourseItem(state, payload) {
                const parentItem = findItemByUuid(state.form.course.items, payload.parent);
                if (parentItem) {
                    if (!Array.isArray(parentItem.items)) parentItem.items = [];
                    const targetArray = parentItem.items;
                    const hasExplicitOrder = typeof payload.order === 'number' && !Number.isNaN(payload.order);
                    const insertIndex = hasExplicitOrder
                        ? Math.max(0, Math.min(payload.order, targetArray.length))
                        : targetArray.length;
                    targetArray.splice(insertIndex, 0, payload);
                    // Recalculate order for siblings including the inserted item
                    for (let i = 0; i < targetArray.length; i++) {
                        targetArray[i].order = i;
                    }
                } else {
                    const targetArray = state.form.course.items;
                    const hasExplicitOrder = typeof payload.order === 'number' && !Number.isNaN(payload.order);
                    const insertIndex = hasExplicitOrder
                        ? Math.max(0, Math.min(payload.order, targetArray.length))
                        : targetArray.length;
                    targetArray.splice(insertIndex, 0, payload);
                    for (let i = 0; i < targetArray.length; i++) {
                        targetArray[i].order = i;
                    }
                }
            },

            insertCourseItemAtPosition(state, { item, index }) {
                state.form.course.items.splice(index, 0, item);

                item.order = index;

                for (let i = index + 1; i < state.form.course.items.length; i++) {
                    state.form.course.items[i].order = i;
                }
            },

            addSectionItem(state, payload) {
                const parentItem = findItemByUuid(state.form.course.items, payload.parent);
                if (parentItem) {
                    if (!Array.isArray(parentItem.items)) parentItem.items = [];
                    const targetArray = parentItem.items;
                    const hasExplicitOrder = typeof payload.order === 'number' && !Number.isNaN(payload.order);
                    const insertIndex = hasExplicitOrder
                        ? Math.max(0, Math.min(payload.order, targetArray.length))
                        : targetArray.length;
                    targetArray.splice(insertIndex, 0, payload);
                    // Recalculate order for siblings including the inserted item
                    for (let i = 0; i < targetArray.length; i++) {
                        targetArray[i].order = i;
                    }
                } else {
                    const targetArray = state.form.course.items;
                    const hasExplicitOrder = typeof payload.order === 'number' && !Number.isNaN(payload.order);
                    const insertIndex = hasExplicitOrder
                        ? Math.max(0, Math.min(payload.order, targetArray.length))
                        : targetArray.length;
                    targetArray.splice(insertIndex, 0, payload);
                    for (let i = 0; i < targetArray.length; i++) {
                        targetArray[i].order = i;
                    }
                }
            },

            deleteCourseItem(state, item) {
                const search = new RecursiveSearch();

                search.delete(state.form.course.items, {
                    ...searchParams,
                    subjectKey: item.uuid,
                });
            },

            fill(state, payload) {
                state.form.course = payload;
            },

            SET_HYDRATING(state, hydrating) {
                state.hydrating = hydrating;
            },

            insert(state, { payload }) {
                state.form.course.items = payload.array;
            },

            insertDuplicate(state, payload) {
                state.form.course.items.splice(payload.order, 0, payload);
            },

            reIndex(state) {
                const search = new RecursiveSearch();

                search.update(state.form.course.items, {
                    ...searchParams,
                    parentId: null,
                    action: (target, parentId, index) => {
                        if (target.order !== index) {
                            target.order = index;
                        }

                        if (target.parent !== parentId && parentId) {
                            target.parent = parentId;
                        }

                        if (!parentId) {
                            delete target.parent;
                        }
                    },
                });
            },

            reset(state) {
                state.hydrating = false;
                state.form = {
                    course: {
                        id: '',
                        title: '',
                        description: '',
                        course_type_id: null,
                        course_settings_template: {
                            id: null,
                            course_key_enabled: false,
                            has_flexible_attendance: false,
                            show_student_enrollments: true,
                            permit_extra_credit_overflow: false,
                            attendance_scenario_id: null,
                        },
                        grading_scheme_template_id: null,
                        external_id: null,
                        items: [],
                        tags: [],
                        groups: [],
                        course_codes: [],
                    },
                };
            },

            updateCourseField(state, { field, value }) {
                set(state.form.course, field, value);
            },

            toggleCourseKey(state, { field, enabled }) {
                set(state.form.course, field, enabled);
            },

            setExxatCourseId(state, { field, value }) {
                set(state.form.course, field, value);
            },

            SET_COURSE_KEY_INTEGRATION_IN_PROGRESS(state, inProgress) {
                state.courseKeyIntegrationInProgress = inProgress;
            },

            SET_COURSE_KEY_INTEGRATION_ERROR(state, error) {
                state.courseKeyIntegrationError = error;
            },

            setAssignableTags(state, tags) {
                state.assignableTags = tags;
            },

            pendingScroll(state, item) {
                state.pendingScroll = item;
            },

            MOVE_ITEMS_TO_TARGET(state, { itemIds, targetId }) {
                const itemsToMove = [];
                const search = new RecursiveSearch();

                itemIds.forEach(itemId => {
                    const item = search.get(state.form.course.items, {
                        ...searchParams,
                        subjectKey: itemId,
                    });

                    if (item) {
                        itemsToMove.push(item);
                        // Remove from current position
                        search.delete(state.form.course.items, {
                            ...searchParams,
                            subjectKey: itemId,
                        });
                    }
                });

                const targetParent = search.get(state.form.course.items, {
                    ...searchParams,
                    subjectKey: targetId,
                });

                if (targetParent) {
                    if (!targetParent.items) {
                        targetParent.items = [];
                    }

                    itemsToMove.forEach(item => {
                        if (item.namespace === 'Activities' || item.namespace === 'ActivityTemplates') {
                            item.section_id = targetId;
                            item.section_template_id = targetId;
                        }

                        targetParent.items.push(item);
                    });
                } else {
                    itemsToMove.forEach(item => {
                        state.form.course.items.push(item);
                    });
                }

                search.update(state.form.course.items, {
                    ...searchParams,
                    parentId: null,
                    action: (target, parentId, index) => {
                        if (target.order !== index) {
                            target.order = index;
                        }
                        if (target.parent !== parentId && parentId) {
                            target.parent = parentId;
                        }
                        if (!parentId) {
                            delete target.parent;
                        }
                    },
                });
            },
        },
    };
}
